import axios from "axios";

export const API_BASE =
	process.env.REACT_APP_API_URL || "http://localhost/api-smartpet";

export const API_URL = API_BASE;

const apiClient = axios.create({
	baseURL: API_BASE,
	headers: {
		"Content-Type": "application/json",
	},
});

// Interceptor para agregar X-User-Id si existe
apiClient.interceptors.request.use((config) => {
	const userStr = localStorage.getItem("user");
	if (userStr) {
		try {
			const user = JSON.parse(userStr);
			if (user?.id) {
				config.headers["X-User-Id"] = String(user.id);
			}
		} catch {
			// ignorar
		}
	}
	return config;
});

// ============================================================
// FUNCIONES DE UTILIDAD PARA IMÁGENES
// ============================================================
export const getImageUrl = (
	url,
	placeholder = "/assets/smartpet-default.jpg",
) => {
	if (!url || url === "null" || url === "undefined") {
		return placeholder;
	}
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	return `${API_BASE}/${url.replace(/^\/+/, "")}`;
};

export const withCacheBust = (url, version) => {
	if (!url) return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}v=${version || Date.now()}`;
};

// ============================================================
// API CLIENT
// ============================================================
export const api = {
	// ═══════════════════════════════════════════════════════════
	// AUTH
	// ═══════════════════════════════════════════════════════════
	login: (email, password) =>
		apiClient.post("/index.php/login", { email, password }),

	register: (userData) => apiClient.post("/index.php/usuarios", userData),

	requestReset: (email) =>
		apiClient.post("/index.php/request-reset", { email }),

	resetPassword: (token, newPassword) =>
		apiClient.post("/index.php/reset-password", {
			token,
			new_password: newPassword,
		}),

	// ═══════════════════════════════════════════════════════════
	// USUARIOS
	// ═══════════════════════════════════════════════════════════
	getUser: (id) => apiClient.get(`/index.php/usuarios/${id}`),
	updateUser: (id, data) => apiClient.put(`/index.php/usuarios/${id}`, data),
	deleteUser: (id) => apiClient.delete(`/index.php/usuarios/${id}`),

	uploadPerfil: (id, file) => {
		const formData = new FormData();
		formData.append("imagen", file);
		return apiClient.post(`/index.php/upload-perfil/${id}`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	changePassword: (usuario_id, current_password, new_password) =>
		apiClient.post("/index.php/change-password", {
			usuario_id,
			current_password,
			new_password,
		}),

	// ═══════════════════════════════════════════════════════════
	// MASCOTAS
	// ═══════════════════════════════════════════════════════════
	getMascotas: () => apiClient.get("/index.php/mascotas"),
	getMascota: (id) => apiClient.get(`/index.php/mascotas/${id}`),
	createMascota: (data) => apiClient.post("/index.php/mascotas", data),
	updateMascota: (id, data) =>
		apiClient.put(`/index.php/mascotas/${id}`, data),
	deleteMascota: (id) => apiClient.delete(`/index.php/mascotas/${id}`),

	uploadMascotaFoto: (id, file, extraData = {}) => {
		const formData = new FormData();
		formData.append("imagen", file);
		Object.entries(extraData).forEach(([key, val]) => {
			if (val !== undefined && val !== null) {
				formData.append(key, val);
			}
		});
		return apiClient.post(`/index.php/upload-mascota/${id}`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	// ═══════════════════════════════════════════════════════════
	// USER-CODES (CHAPITAS)
	// ═══════════════════════════════════════════════════════════
	getUserCodes: (usuarioId) =>
		apiClient.get(`/index.php/user-codes?usuario_id=${usuarioId}`),

	vincularCodigo: (usuarioId, codigoUnico) =>
		apiClient.post("/index.php/user-codes", {
			usuario_id: usuarioId,
			codigo_unico: codigoUnico,
		}),

	desvincularCodigo: (id) => apiClient.delete(`/index.php/user-codes/${id}`),

	// ═══════════════════════════════════════════════════════════
	// PRODUCTOS
	// ═══════════════════════════════════════════════════════════
	getProductos: () => apiClient.get("/index.php/productos"),
	getProducto: (id) => apiClient.get(`/index.php/productos/${id}`),
	createProducto: (data) => apiClient.post("/index.php/productos", data),
	updateProducto: (id, data) =>
		apiClient.put(`/index.php/productos/${id}`, data),
	deleteProducto: (id) => apiClient.delete(`/index.php/productos/${id}`),

	// ═══════════════════════════════════════════════════════════
	// CONTACTOS MASCOTA
	// ═══════════════════════════════════════════════════════════
	getContactos: () => apiClient.get("/index.php/contactos"),
	getContacto: (id) => apiClient.get(`/index.php/contactos/${id}`),
	createContacto: (data) => apiClient.post("/index.php/contactos", data),
	updateContacto: (id, data) =>
		apiClient.put(`/index.php/contactos/${id}`, data),
	deleteContacto: (id) => apiClient.delete(`/index.php/contactos/${id}`),

	getContactosByMascota: (mascotaId) =>
		apiClient.get(`/index.php/contactos?mascota_id=${mascotaId}`),

	// ═══════════════════════════════════════════════════════════
	// HISTORIAL MASCOTA
	// ═══════════════════════════════════════════════════════════
	getHistorial: () => apiClient.get("/index.php/historial"),
	getHistorialItem: (id) => apiClient.get(`/index.php/historial/${id}`),
	createHistorial: (data) => apiClient.post("/index.php/historial", data),
	updateHistorial: (id, data) =>
		apiClient.put(`/index.php/historial/${id}`, data),
	deleteHistorial: (id) => apiClient.delete(`/index.php/historial/${id}`),

	// ✅ Método específico para obtener eventos por mascota (usando id_mascota)
	getHistorialByMascota: (mascotaId) =>
		apiClient.get(`/index.php/historial?id_mascota=${mascotaId}`),

	// ═══════════════════════════════════════════════════════════
	// UBICACIONES
	// ═══════════════════════════════════════════════════════════
	getUbicaciones: () => apiClient.get("/index.php/ubicaciones"),
	getUbicacion: (id) => apiClient.get(`/index.php/ubicaciones/${id}`),
	createUbicacion: (data) => apiClient.post("/index.php/ubicaciones", data),
	updateUbicacion: (id, data) =>
		apiClient.put(`/index.php/ubicaciones/${id}`, data),
	deleteUbicacion: (id) => apiClient.delete(`/index.php/ubicaciones/${id}`),

	getUbicacionesByMascota: (mascotaId) =>
		apiClient.get(`/index.php/ubicaciones?mascota_id=${mascotaId}`),

	// ═══════════════════════════════════════════════════════════
	// SOCIOS
	// ═══════════════════════════════════════════════════════════
	getSocios: () => apiClient.get("/index.php/socios"),
	getSocio: (id) => apiClient.get(`/index.php/socios/${id}`),
	createSocio: (data) => apiClient.post("/index.php/socios", data),
	updateSocio: (id, data) => apiClient.put(`/index.php/socios/${id}`, data),
	deleteSocio: (id) => apiClient.delete(`/index.php/socios/${id}`),

	// ═══════════════════════════════════════════════════════════
	// CODIGOS (ADMIN)
	// ═══════════════════════════════════════════════════════════
	getCodigos: () => apiClient.get("/index.php/codigos"),
	getCodigo: (id) => apiClient.get(`/index.php/codigos/${id}`),
	createCodigo: (data) => apiClient.post("/index.php/codigos", data),
	updateCodigo: (id, data) => apiClient.put(`/index.php/codigos/${id}`, data),
	deleteCodigo: (id) => apiClient.delete(`/index.php/codigos/${id}`),
};

export default api;
